import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

interface StoryInputProps {
  story: string | undefined;
  onChange: (story: string) => void;
  disabled?: boolean;
}

export function StoryInput({ story, onChange, disabled = false }: StoryInputProps) {
  const [localValue, setLocalValue] = useState<string>(story || '');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);
  const lastSentValueRef = useRef<string>(story || '');
  const editorRef = useRef<HTMLDivElement>(null);

  // Initialize editor content on mount
  useEffect(() => {
    if (editorRef.current && story) {
      editorRef.current.innerHTML = story;
    }
  }, []);

  // Update local value when prop changes (from server), but only if user isn't typing
  // or if the server value matches what we last sent (meaning our update was successful)
  useEffect(() => {
    const storyValue = story || '';
    if (!isTypingRef.current || story === lastSentValueRef.current) {
      setLocalValue(storyValue);
      // Update the contentEditable div content
      if (editorRef.current && storyValue !== editorRef.current.innerHTML) {
        editorRef.current.innerHTML = storyValue;
      }
      if (story === lastSentValueRef.current) {
        // Our update was successful, safe to reset typing flag
        isTypingRef.current = false;
      }
    }
    // Initialize lastSentValueRef on mount or when story changes externally
    if (story !== lastSentValueRef.current && !isTypingRef.current) {
      lastSentValueRef.current = story || '';
    }
  }, [story]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleInput = () => {
    if (!editorRef.current) return;
    
    const newValue = editorRef.current.innerHTML;
    setLocalValue(newValue);
    isTypingRef.current = true;

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the onChange callback
    debounceTimerRef.current = setTimeout(() => {
      lastSentValueRef.current = newValue;
      onChange(newValue);
      // Typing flag will be reset when server confirms the update
    }, 500); // 500ms debounce
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    // Get clipboard data - prefer HTML, fallback to plain text
    const clipboardData = e.clipboardData;
    let pastedData = clipboardData.getData('text/html');
    
    // If no HTML, convert plain text to HTML (preserve line breaks)
    if (!pastedData) {
      const plainText = clipboardData.getData('text/plain');
      // Convert plain text to HTML, preserving line breaks
      pastedData = plainText
        .split('\n')
        .map((line) => (line.trim() ? `<p>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>` : '<br>'))
        .join('');
    }
    
    if (!editorRef.current) return;

    // Get selection
    const selection = window.getSelection();
    if (!selection) return;

    let range: Range;
    if (selection.rangeCount === 0) {
      // If no selection, append to end
      range = document.createRange();
      if (editorRef.current.lastChild) {
        range.setStartAfter(editorRef.current.lastChild);
      } else {
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
      }
      range.collapse(true);
    } else {
      range = selection.getRangeAt(0);
    }

    range.deleteContents();

    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = pastedData;

    // Sanitize the HTML content
    const sanitizedHTML = DOMPurify.sanitize(tempDiv.innerHTML, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code', 'div', 'span'],
      ALLOWED_ATTR: ['style', 'class'],
    });

    // Create a document fragment and insert sanitized content
    const fragment = document.createDocumentFragment();
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = sanitizedHTML;
    
    while (tempContainer.firstChild) {
      fragment.appendChild(tempContainer.firstChild);
    }

    range.insertNode(fragment);
    
    // Move cursor to end of inserted content
    if (fragment.lastChild) {
      range.setStartAfter(fragment.lastChild);
    }
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    // Trigger input event to update state
    handleInput();
  };

  return (
    <div>
      <label htmlFor="story" className="block text-sm font-medium text-gray-700 mb-2">
        Story Description (Optional)
      </label>
      <div
        ref={editorRef}
        id="story"
        contentEditable={!disabled}
        onInput={handleInput}
        onPaste={handlePaste}
        suppressContentEditableWarning
        data-placeholder="Enter story description..."
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[80px] outline-none"
        style={{
          minHeight: '80px',
        }}
      />
      <style>{`
        #story:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        #story:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
}


